import {
  faEye,
  faCircleXmark,
  faCircleCheck
} from "@fortawesome/free-solid-svg-icons";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import {
  TableRow,
  CustomCard,
  PageHeading,
  HeaderColumn,
  BootstrapTable,
  TableDetailCell,
  LastHeaderColumn,
  FirstHeaderColumn,
  TableDetailNameCell
} from "../common-styling";
import {
  fetchBlockedZipTasks,
  updateBlockedZipTask
} from "../../services/api-declaration";
import TableLoader from "../loader/table-loader";
import NoResultsRow from "../no-results-table-row";
import { showError } from "../../services/toast-service";
import CustomPagination from "../pagination/custom-pagination";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { BLOCKED_LEAD_TASK_TYPE, DATE_REVERSE_FORMAT } from "../strings";
import AdvancedFilters from "../table-filter/advanced-filter";
import { blockedZipFilterConfig } from "../funnels-table/filter-config";

const ZipCheck = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [blockedZipData, setBlockedZipData] = useState([]);
  const [paginateOpts, setPaginateOpts] = useState({});
  const [loadingById, setLoadingById] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const colSpan = 7;
  const [show, setShow] = useState(false);

  const toggleModal = () => setShow(!show);

  const handleQueryParams = (property, value) => {
    searchParams.set(property, value);
    setSearchParams(searchParams);
  };

  const getDate = (item) => {
    return item.type === BLOCKED_LEAD_TASK_TYPE.DAILY
      ? moment(item.createdAt).subtract(1, "day").format(DATE_REVERSE_FORMAT)
      : `${moment(item.createdAt)
          .subtract(1, "week")
          .startOf("isoWeek")
          .format(DATE_REVERSE_FORMAT)} to ${moment(item.createdAt)
          .subtract(1, "week")
          .endOf("isoWeek")
          .format(DATE_REVERSE_FORMAT)}`;
  };

  const showReportType = (type) => {
    switch (type) {
      case BLOCKED_LEAD_TASK_TYPE.DAILY:
        return "Daily";
      case BLOCKED_LEAD_TASK_TYPE.WEEKLY:
        return "Weekly";
      default:
        return "";
    }
  };

  const onSave = async (status, item) => {
    const id = item._id;
    const data = {
      ...item,
      status
    };
    const load = { ...loadingById };
    load[id] = "loading";
    setLoadingById(load);
    try {
      const response = await updateBlockedZipTask(id, data);
      if (response.data.data) {
        load[id] = "hide";
      }
      setLoadingById({ ...load });
    } catch (error) {
      load[id] = "failed";
      showError(error?.response?.data?.message);
      setLoadingById({ ...load });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const params = {};
        for (const [key, value] of searchParams.entries()) {
          params[key] = value;
        }
        const response = await fetchBlockedZipTasks(params);
        const respData = response.data.data;

        if (!respData.data.length) {
          setBlockedZipData([]);
          setPaginateOpts({});
          return;
        }
        setBlockedZipData(respData.data);
        setPaginateOpts({
          ...respData.metadata,
          rowsOnPage: respData.data.length
        });
      } catch (error) {
        showError(
          error?.response?.data?.message ||
            t("toastMessage.errorInGetting", {
              what: t("sidebar.blockPostalCode")
            })
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [searchParams, t]);

  return (
    <>
      <div>
        <PageHeading>{t("sidebar.blockPostalCode")}</PageHeading>
        <div className="mt-3">
          <AdvancedFilters
            components={blockedZipFilterConfig}
            pageName="name"
            handleModal={toggleModal}
            hideAddNew
            hideSearchFilter
          />
        </div>
        <CustomCard className="mt-4 mb-3 p-3 rounded-0">
          <BootstrapTable responsive>
            <thead>
              <tr>
                <FirstHeaderColumn colSpan={2}>
                  Funnel {t("common.name")}
                </FirstHeaderColumn>
                <HeaderColumn className="text-center">
                  {t("common.reportType")}
                </HeaderColumn>
                <HeaderColumn className="text-center">
                  {t("common.blockedCount")}
                </HeaderColumn>
                <HeaderColumn className="text-center" colSpan={2}>
                  {t("common.blockedOn")}
                </HeaderColumn>
                <LastHeaderColumn className="text-end align-middle px-5">
                  {t("common.action")}
                </LastHeaderColumn>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableLoader colSpan={colSpan} />
              ) : blockedZipData.length ? (
                blockedZipData.map((item) => (
                  <TableRow key={item._id} className="optimization-table-row">
                    <TableDetailNameCell className="py-3" colSpan={2}>
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip>
                            {item.funnelId?.general?.funnelName}
                          </Tooltip>
                        }
                      >
                        <span>{item.funnelId?.general?.funnelName}</span>
                      </OverlayTrigger>
                    </TableDetailNameCell>
                    <TableDetailCell className="py-3 text-center">
                      {showReportType(item.type)}
                    </TableDetailCell>
                    <TableDetailCell className="py-3 text-center">
                      {item.leadCount}
                    </TableDetailCell>
                    <TableDetailCell className="py-3 text-center" colSpan={2}>
                      {getDate(item)}
                    </TableDetailCell>
                    <TableDetailCell className="text-end align-middle px-3">
                      {loadingById[item._id] === "hide" ? (
                        <></>
                      ) : loadingById[item._id] === "loading" ? (
                        <Spinner variant="primary" animation="border" />
                      ) : item.funnelId?.hubspotProjectId ? (
                        <>
                          {item.funnelId && (
                            <Link
                              style={{ color: "inherit" }}
                              target="_blank"
                              to={`/dashboard/funnels/${item.funnelId?._id}/form?funnelType=${item.funnelId?.general?.funnelType}`}
                            >
                              <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip>View Funnel</Tooltip>}
                              >
                                <FontAwesomeIcon
                                  size="xl"
                                  className="me-2 cursor-pointer"
                                  icon={faEye}
                                />
                              </OverlayTrigger>
                            </Link>
                          )}
                          <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip>Create Task</Tooltip>}
                          >
                            <FontAwesomeIcon
                              size="xl"
                              className="me-2 t-success cursor-pointer"
                              icon={faCircleCheck}
                              onClick={() => {
                                onSave("CREATED", item);
                              }}
                            />
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip>Ingore Task</Tooltip>}
                          >
                            <FontAwesomeIcon
                              size="xl"
                              className="me-2 t-danger cursor-pointer"
                              icon={faCircleXmark}
                              onClick={() => {
                                onSave("IGNORED", item);
                              }}
                            />
                          </OverlayTrigger>
                        </>
                      ) : (
                        <Link
                          style={{ color: "inherit" }}
                          target="_blank"
                          to={`/dashboard/funnels/${item.funnelId}/form?funnelType=${item.funnelType}`}
                        >
                          <p className="m-0">Link Hubspot</p>
                        </Link>
                      )}
                    </TableDetailCell>
                  </TableRow>
                ))
              ) : (
                <NoResultsRow lengthOfColumns={colSpan} />
              )}
            </tbody>
          </BootstrapTable>
        </CustomCard>
        {blockedZipData.length ? (
          <CustomPagination
            paginateOpts={paginateOpts}
            activeTab={Number(searchParams.get("pageIndex")) || 1}
            select={(val) => handleQueryParams("pageIndex", val)}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
export default ZipCheck;
